import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Flame, Snowflake, TrendingUp, MapPin, BarChart3 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LaneIntelligencePage = () => {
  const [days, setDays] = useState(7);
  const [summary, setSummary] = useState(null);
  const [hot, setHot] = useState([]);
  const [cold, setCold] = useState([]);
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const [s, h, c, st] = await Promise.all([
          axios.get(`${API}/lane-intelligence/summary?days=${days}`),
          axios.get(`${API}/lane-intelligence/hot-lanes?days=${days}&limit=15`),
          axios.get(`${API}/lane-intelligence/cold-lanes?days=${days}&limit=15`),
          axios.get(`${API}/lane-intelligence/state-demand?days=${days}`),
        ]);
        if (alive) {
          setSummary(s.data);
          setHot(h.data.hot_lanes || []);
          setCold(c.data.cold_lanes || []);
          setStates(st.data.states || []);
        }
      } catch (e) {
        console.error(e);
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => { alive = false; };
  }, [days]);

  if (loading) return <Layout><div className="p-8 text-center text-muted-foreground">Loading lane intelligence…</div></Layout>;

  const maxStateLoads = Math.max(...states.map(s => s.loads), 1);

  return (
    <Layout>
      <div className="p-4 lg:p-8 space-y-6" data-testid="lane-intelligence-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl lg:text-4xl font-bold flex items-center gap-3">
              <BarChart3 className="w-8 h-8 text-blue-400" />
              Lane Intelligence
            </h1>
            <p className="text-muted-foreground text-sm mt-1">Where to run · where to avoid · live market signals</p>
          </div>
          <div className="flex gap-2">
            {[7, 30, 90].map(d => (
              <Button
                key={d}
                variant={days === d ? 'default' : 'outline'}
                size="sm"
                onClick={() => setDays(d)}
                data-testid={`days-${d}`}
              >{d}d</Button>
            ))}
          </div>
        </div>

        {/* SUMMARY */}
        {summary && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card><CardContent className="p-5">
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Loads ({days}d)</div>
              <div className="text-3xl font-bold">{summary.loads.toLocaleString()}</div>
            </CardContent></Card>
            <Card><CardContent className="p-5">
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Avg Rate</div>
              <div className="text-3xl font-bold text-green-400">${Math.round(summary.avg_rate).toLocaleString()}</div>
            </CardContent></Card>
            <Card><CardContent className="p-5">
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Avg $/Mile</div>
              <div className="text-3xl font-bold">${summary.avg_rpm.toFixed(2)}</div>
            </CardContent></Card>
            <Card><CardContent className="p-5">
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Best Lane $/Mi</div>
              <div className="text-3xl font-bold text-amber-400">${summary.max_rpm.toFixed(2)}</div>
            </CardContent></Card>
          </div>
        )}

        {/* HOT + COLD */}
        <div className="grid lg:grid-cols-2 gap-6">
          <Card data-testid="hot-lanes-card" className="border-orange-500/20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Flame className="w-5 h-5 text-orange-400" /> Hot lanes (highest $/mi)</CardTitle>
            </CardHeader>
            <CardContent>
              {hot.length === 0 ? (
                <div className="text-center py-10 text-muted-foreground text-sm">Not enough load history yet. Engage Autopilot and post more loads to unlock insights.</div>
              ) : (
                <div className="space-y-1.5">
                  {hot.map((l) => (
                    <div key={l.lane} className="flex items-center justify-between p-2.5 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                      <div className="flex items-center gap-2 min-w-0">
                        <MapPin className="w-4 h-4 text-orange-400 shrink-0" />
                        <span className="font-medium text-sm truncate">{l.lane}</span>
                        <Badge variant="outline" className="text-[10px]">{l.loads}</Badge>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-green-400">${l.avg_rpm.toFixed(2)}<span className="text-xs text-muted-foreground">/mi</span></div>
                        <div className="text-[11px] text-muted-foreground">${Math.round(l.avg_rate).toLocaleString()} avg</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card data-testid="cold-lanes-card" className="border-blue-500/20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Snowflake className="w-5 h-5 text-blue-400" /> Cold lanes (avoid · low $/mi)</CardTitle>
            </CardHeader>
            <CardContent>
              {cold.length === 0 ? (
                <div className="text-center py-10 text-muted-foreground text-sm">No cold lanes detected yet</div>
              ) : (
                <div className="space-y-1.5">
                  {cold.map((l) => (
                    <div key={l.lane} className="flex items-center justify-between p-2.5 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                      <div className="flex items-center gap-2 min-w-0">
                        <MapPin className="w-4 h-4 text-blue-400 shrink-0" />
                        <span className="font-medium text-sm truncate">{l.lane}</span>
                        <Badge variant="outline" className="text-[10px]">{l.loads}</Badge>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-red-400">${l.avg_rpm.toFixed(2)}<span className="text-xs text-muted-foreground">/mi</span></div>
                        <div className="text-[11px] text-muted-foreground">${Math.round(l.avg_rate).toLocaleString()} avg</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* STATE DEMAND */}
        <Card data-testid="state-demand-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><TrendingUp className="w-5 h-5" /> Load origin demand by state ({days}d)</CardTitle>
          </CardHeader>
          <CardContent>
            {states.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground text-sm">No data yet</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {states.map((s) => (
                  <div key={s.state} className="p-3 rounded-lg bg-muted/30">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="font-bold">{s.state}</span>
                      <span className="text-xs text-muted-foreground">{s.share_pct}%</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full" style={{ width: `${(s.loads / maxStateLoads) * 100}%` }} />
                    </div>
                    <div className="flex items-center justify-between mt-1.5 text-xs">
                      <span className="text-muted-foreground">{s.loads} loads</span>
                      <span className="text-green-400">${Math.round(s.avg_rate).toLocaleString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default LaneIntelligencePage;
